import React from 'react'
import { useParams } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { LICENSE_REQUEST, TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { clientLibraryUpdate } from '@disco/state-management/actions/client-library-list-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import {
  getBusinessDefaultZipMode,
  isClientLibrariesTrial,
} from '@disco/state-management/selectors/user'

import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'

import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import ContactUsLink from 'components/ui/contact-us-link'
import PageContainer from 'components/ui/page-container'

import { useFormState } from 'hooks/use-form-state'
import { useDispatch, useSelector } from 'hooks/use-redux'

type FormFields =
  | 'enable_license_request'
  | 'include_nested_track_versions'
  | 'include_only_optimized_formats'
  | 'share_stems'
const FORM_FIELDS: FormFields[] = [
  'enable_license_request',
  'include_nested_track_versions',
  'include_only_optimized_formats',
  'share_stems',
]
// const EN_SPACE = ' '

const LibrariesLibrarySettings = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const dispatch = useDispatch()
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const clientLibrariesTrial: boolean = useSelector(isClientLibrariesTrial)
  const defaultZipMode = useSelector(getBusinessDefaultZipMode)
  const licenseRequestEnabled: boolean = useFeatureEnabled(LICENSE_REQUEST)
  const isNestingEnabled: boolean = useFeatureEnabled(TRACK_NESTING)
  const [formState, setFormState, pendingChanges] = useFormState(
    library,
    FORM_FIELDS,
  )

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(clientLibraryUpdate(libraryId, formState))
  }

  return (
    <PageContainer>
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | General settings | DISCO`}
        />
      )}

      {licenseRequestEnabled && (
        <PageContainer.Section title="License request form">
          <Row columnsSize={[9]}>
            <div>
              <ChoiceList
                disabled={library && library['loading']}
                title={
                  <>
                    Enabling a license request form will allow any visitor to
                    your Catalog to easily submit an inquiry to license the
                    music from your Catalog. This will generate an email thread
                    between the license requester and anyone listed to receive
                    notifications about your Catalog.
                    <br />
                    Click{' '}
                    <a
                      className="text-link"
                      href="https://support.disco.ac/home/catalogs-guide#enabling-license-request-forms-on-your-catalog"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </a>{' '}
                    to view the form specifications.
                  </>
                }
                items={[
                  {
                    label: 'Enable license request form',
                    selected: formState['enable_license_request'],
                    value: 'enable_license_request',
                  },
                ]}
                onChange={(value: FormFields, checked: boolean) => {
                  mixpanel.sendPureEvent('License request settings changed', {
                    enable: checked,
                  })
                  setFormState(value, checked)
                }}
              />
            </div>
          </Row>
        </PageContainer.Section>
      )}

      <PageContainer.Section title="Format settings">
        <Row columnsSize={[9]}>
          <div>
            <ChoiceList
              disabled={
                (library && library['loading']) ||
                defaultZipMode === 'original_else_optimized'
              }
              title="If you’d like only the optimised format of your tracks available for download (MP3s and M4As), and not the hi-res formats (wavs and aiffs), select the “Include only optimised formats” option below."
              items={[
                {
                  label: 'Include only optimised formats',
                  selected: formState['include_only_optimized_formats'],
                  value: 'include_only_optimized_formats',
                  tooltipContent: defaultZipMode ===
                    'original_else_optimized' && (
                    <>
                      This setting is controlled by your business download
                      settings. <ContactUsLink>Contact support</ContactUsLink>{' '}
                      to change.
                    </>
                  ),
                },
              ]}
              onChange={setFormState}
            />
          </div>
        </Row>
      </PageContainer.Section>

      {isNestingEnabled && (
        <PageContainer.Section title="Nesting controls">
          <Row columnsSize={[9]}>
            <div>
              <ChoiceList
                items={[
                  {
                    label: 'Include nested track versions',
                    selected: formState['include_nested_track_versions'],
                    value: 'include_nested_track_versions',
                    tooltipContent:
                      'Check this if you’d like to include nested versions of your tracks in this Catalog',
                  },
                  {
                    label: 'Share stems',
                    selected: formState['share_stems'],
                    value: 'share_stems',
                    tooltipContent:
                      'Check this if you’d like to include stems associated with your tracks in this Catalog',
                  },
                ]}
                onChange={setFormState}
              />
            </div>
          </Row>
        </PageContainer.Section>
      )}

      <PageContainer.Section
        title="Custom domain"
        description={
          clientLibrariesTrial ? (
            'Want to use your own domain for your Catalog? Custom domains are available to Catalog subscribers.'
          ) : (
            <>
              Want to use your own domain for your Catalog?{' '}
              <a
                className="text-link"
                href="https://form.asana.com/?k=5zAQI0aoismJluz_qmi9RQ&d=35126173840529"
                target="_blank"
                rel="noopener noreferrer"
              >
                Please fill out this form.
              </a>{' '}
              Please note custom domains are limited to a maximum of three per
              DISCO account.
            </>
          )
        }
      />

      {/* TODO: Return when the feature will be available https://app.asana.com/0/1106437503322489/1174627694356545/f */}
      {/* <PageContainer.Section
       title={
         <>
           Metadata settings
           {EN_SPACE}
           <Lozenge color="yellow" text="Coming soon" />
         </>
       }
      >
       <Row columnsSize={[6, 6]}>
         <div>
           <ChoiceList
             disabled={true}
             title="Include the following metadata for search and download on this library"
             items={[
               {
                 label: 'Artist',
                 value: 'Artist',
               },
               {
                 label: 'Album',
                 value: 'Album',
               },
               {
                 label: 'Genre',
                 value: 'Genre',
               },
               {
                 label: 'Composer',
                 value: 'Composer',
               },
               {
                 label: 'Grouping',
                 value: 'Grouping',
               },
               {
                 label: 'Comments',
                 value: 'Comments',
               },
             ]}
           />
         </div>
         <div>
           <ChoiceList
             disabled={true}
             multiple={false}
             title="Include metadata that DISCO does not display"
             items={[
               {
                 label: 'Yes',
                 value: true,
               },
               {
                 label: 'No',
                 value: false,
               },
             ]}
           />
         </div>
       </Row>
       <Row columnsSize={[10]} marginBottom={false}>
         <div>
           <TextField
             disabled={true}
             label="Enter a custom message in the Comments metadata"
             multiline={5}
           />
         </div>
       </Row>
      </PageContainer.Section> */}
      <PageContainer.Footer>
        <ButtonContainer>
          <Button
            appearance="primary"
            caption="Update Catalog settings"
            disabled={!library || library.loading || !pendingChanges}
            onClick={handleSubmit}
          />
        </ButtonContainer>
      </PageContainer.Footer>
    </PageContainer>
  )
}

export default LibrariesLibrarySettings
